import React, { createContext, useCallback, useContext, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  useAcknowledgeBackgroundJobMutation,
  useCancelBackgroundJobMutation,
  usePendingBackgroundJobsForUserQuery,
} from 'src/fetch/backgroundJobs/backgroundJobs'
import { useIsGov, useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey } from 'src/utils/queryClient'

import { buildTrackedJobsFromPendingRows } from './trackedBackgroundJob'
import { TrackedBackgroundJob } from './types'

type BackgroundJobsContextValue = {
  trackedJobs: TrackedBackgroundJob[]
  /** Refreshes pending jobs after initiateBulkJob (auto-ack and onComplete refetch ship in a follow-up PR). */
  registerJob: () => void
  cancelJob: (jobId: number) => Promise<void>
  /** Acknowledges a terminal job so it drops out of pendingBackgroundJobsForUser. */
  dismissJob: (jobId: number) => Promise<void>
}

const BackgroundJobsContext = createContext<BackgroundJobsContextValue | null>(null)

type Props = {
  children?: React.ReactNode
}

const BackgroundJobsProvider = ({ children }: Props) => {
  const isGov = useIsGov()
  const activeOrgId = useMyOrgId()
  const reactQueryClient = useQueryClient()
  const cancelBackgroundJobMutation = useCancelBackgroundJobMutation()
  const acknowledgeBackgroundJobMutation = useAcknowledgeBackgroundJobMutation()

  const { data: pendingBackgroundJobsData } = usePendingBackgroundJobsForUserQuery({
    organizationId: activeOrgId,
    enabled: isGov,
  })

  const registerJob = useCallback(() => {
    void reactQueryClient.invalidateQueries({
      queryKey: [QueryKey.PendingBackgroundJobsForUser, activeOrgId],
    })
  }, [activeOrgId, reactQueryClient])

  const cancelJob = useCallback(
    async (jobId: number) => {
      await cancelBackgroundJobMutation.mutateAsync({ id: jobId })
      void reactQueryClient.invalidateQueries({
        queryKey: [QueryKey.PendingBackgroundJobsForUser, activeOrgId],
      })
    },
    [activeOrgId, cancelBackgroundJobMutation, reactQueryClient]
  )

  const dismissJob = useCallback(
    async (jobId: number) => {
      await acknowledgeBackgroundJobMutation.mutateAsync({
        input: { id: jobId },
      })
      void reactQueryClient.invalidateQueries({
        queryKey: [QueryKey.PendingBackgroundJobsForUser, activeOrgId],
      })
    },
    [activeOrgId, acknowledgeBackgroundJobMutation, reactQueryClient]
  )

  const trackedJobs = useMemo(() => {
    if (!isGov) {
      return []
    }

    const pendingBackgroundJobs = pendingBackgroundJobsData?.pendingBackgroundJobsForUser ?? []
    return buildTrackedJobsFromPendingRows(pendingBackgroundJobs)
  }, [isGov, pendingBackgroundJobsData?.pendingBackgroundJobsForUser])

  const value = useMemo(
    () => ({
      trackedJobs,
      registerJob,
      cancelJob,
      dismissJob,
    }),
    [cancelJob, dismissJob, registerJob, trackedJobs]
  )

  return <BackgroundJobsContext.Provider value={value}>{children}</BackgroundJobsContext.Provider>
}

export default React.memo(BackgroundJobsProvider)

export const useBackgroundJobs = (): BackgroundJobsContextValue => {
  const context = useContext(BackgroundJobsContext)
  if (!context) {
    throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider')
  }
  return context
}
